import { AlertTriangle } from 'lucide-react'
import { format, parseISO } from 'date-fns'
import { Button } from '@/components/ui/Button'
import type {
  MissingExchangeRateRequirement,
  PayrollFxRequirements,
} from '@/features/payroll/types/payroll.types'

export interface PayrollFxRequirementsCardProps {
  requirements: PayrollFxRequirements
  title?: string
  tone?: 'danger' | 'warning'
  onCreateExchangeRate?: (requirement: MissingExchangeRateRequirement) => void
}

function formatDate(date: string): string {
  try {
    return format(parseISO(date), 'PPP')
  } catch {
    return date
  }
}

export function PayrollFxRequirementsCard({
  requirements,
  title = 'Missing exchange rates',
  tone = 'danger',
  onCreateExchangeRate,
}: PayrollFxRequirementsCardProps): React.ReactElement | null {
  if (requirements.missing.length === 0) return null

  const toneClasses =
    tone === 'danger'
      ? 'border-red-200 bg-red-50 text-red-900 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-100'
      : 'border-amber-200 bg-amber-50 text-amber-950 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-100'
  const subduedClasses =
    tone === 'danger'
      ? 'text-red-700 dark:text-red-300'
      : 'text-amber-700 dark:text-amber-300'

  return (
    <div className={`rounded-lg border p-4 ${toneClasses}`}>
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold">{title}</div>
          <p className={`mt-1 text-sm ${subduedClasses}`}>
            Base currency: {requirements.baseCurrency || 'Unknown'} · Needed as of{' '}
            {formatDate(requirements.asOf)}
          </p>
          <div className="mt-3 space-y-3">
            {requirements.missing.map((requirement) => {
              const key = `${requirement.fromCurrency}-${requirement.toCurrency}-${requirement.asOf}`
              return (
                <div
                  key={key}
                  className="flex flex-col gap-3 rounded-lg border border-current/15 bg-white/60 p-3 dark:bg-black/10 md:flex-row md:items-center md:justify-between"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium">
                      {requirement.fromCurrency} → {requirement.toCurrency}
                    </div>
                    <div className={`mt-1 text-xs ${subduedClasses}`}>
                      Effective by {formatDate(requirement.asOf)} ·{' '}
                      {requirement.affectedEmployeeIds.length} affected employee(s)
                    </div>
                  </div>
                  {onCreateExchangeRate && (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => onCreateExchangeRate(requirement)}
                    >
                      Add rate
                    </Button>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

PayrollFxRequirementsCard.displayName = 'PayrollFxRequirementsCard'
